import type {
  DispatchTripRead,
  TripRead,
} from 'src/common/external/bambi-api/bambiApi';
import {
  useTripsCorrelatedListQuery,
  useTripsRetrieveQuery,
} from 'src/common/external/bambi-api/tripsApi';

export interface WaitAndReturnSibling {
  id: string;
  status: TripRead['status'];
}

export interface UseWaitAndReturnSiblingResult {
  sibling: WaitAndReturnSibling | undefined;
  isWaitAndReturnPair: boolean;
  isLoading: boolean;
}

function hasOppositeFlag(
  candidate: {
    is_wait_and_return_outbound?: boolean;
    is_wait_and_return_inbound?: boolean;
  },
  legIsOutbound: boolean
): boolean {
  return legIsOutbound
    ? !!candidate.is_wait_and_return_inbound
    : !!candidate.is_wait_and_return_outbound;
}

/**
 * Detects whether the trip with the given id is one leg of a wait-and-return
 * (W&R) pair and, if so, returns its sibling leg.
 *
 * Resolution is synchronous whenever the leg is present in `dispatchTrips` (the
 * dispatch response carries both legs with their W&R flags + correlation_id), so
 * on the dispatch route there is no async gap and the normal accept/reject form
 * never flashes before the W&R prompt. When the leg isn't in `dispatchTrips`
 * (e.g. the ROM all-requests view), it falls back to a retrieve (for the leg's
 * flags) plus the correlated-trips query (for the sibling).
 *
 * A W&R pair is only recognized when exactly one candidate (same correlation_id,
 * excludes self) carries the opposite W&R flag — never on correlation_id alone,
 * which recurring round-trips also share. The sibling's status is intentionally
 * NOT filtered here; callers decide whether e.g. a still-`requested` sibling
 * matters to their flow.
 *
 * `isLoading` is true only on the async fallback while detection is in flight
 * (the leg-flags retrieve, and then the sibling correlated query for a W&R
 * leg); the synchronous dispatch-response path is never loading.
 */
export function useWaitAndReturnSibling(
  tripId?: string,
  dispatchTrips?: DispatchTripRead[]
): UseWaitAndReturnSiblingResult {
  const legFromDispatch =
    tripId && dispatchTrips
      ? dispatchTrips.find((trip) => trip.id === tripId)
      : undefined;
  const resolvedFromDispatch = !!legFromDispatch;

  const { data: fetchedTrip, isFetching: isFetchingLeg } =
    useTripsRetrieveQuery(
      { id: tripId || '' },
      { skip: !tripId || resolvedFromDispatch }
    );

  const legFlags = legFromDispatch ?? fetchedTrip;
  const isOutbound = !!legFlags?.is_wait_and_return_outbound;
  const isInbound = !!legFlags?.is_wait_and_return_inbound;
  const isWaitAndReturnLeg = isOutbound || isInbound;

  const { data: correlated, isFetching: isFetchingCorrelated } =
    useTripsCorrelatedListQuery(
      { id: tripId || '' },
      { skip: !tripId || resolvedFromDispatch || !isWaitAndReturnLeg }
    );

  let siblingTrip: DispatchTripRead | TripRead | undefined;
  let isLoading = false;

  if (resolvedFromDispatch) {
    if (isWaitAndReturnLeg) {
      const candidates = (dispatchTrips as DispatchTripRead[]).filter(
        (trip) =>
          trip.id !== tripId &&
          !!trip.correlation_id &&
          trip.correlation_id === legFromDispatch.correlation_id &&
          hasOppositeFlag(trip, isOutbound)
      );
      siblingTrip = candidates.length === 1 ? candidates[0] : undefined;
    }
  } else if (tripId) {
    // Async fallback (no dispatch response, e.g. the ROM all-requests view):
    // the leg's flags come from the retrieve and the sibling from the
    // correlated query. isLoading covers BOTH so callers can gate the normal
    // confirm until detection resolves — otherwise the plain modal renders with
    // an enabled confirm and a quick click bypasses the W&R prompt.
    if (isWaitAndReturnLeg) {
      const candidates = (correlated?.results ?? []).filter((trip) =>
        hasOppositeFlag(trip, isOutbound)
      );
      siblingTrip = candidates.length === 1 ? candidates[0] : undefined;
    }
    isLoading = isFetchingLeg || (isWaitAndReturnLeg && isFetchingCorrelated);
  }

  const sibling: WaitAndReturnSibling | undefined = siblingTrip
    ? { id: siblingTrip.id, status: siblingTrip.status }
    : undefined;

  return {
    sibling,
    isWaitAndReturnPair: isWaitAndReturnLeg && !!sibling,
    isLoading,
  };
}
